import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { Bot, Loader2 } from 'lucide-react';

// Extracted sub-components
import ChatHeader from './chat/ChatHeader';
import ChatModeToggle from './chat/ChatModeToggle';
import FaqSection from './chat/FaqSection';
import MessageBubble from './chat/MessageBubble';
import PendingSuggestionCard from './chat/PendingSuggestionCard';
import ChatInput from './chat/ChatInput';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

let _msgIdCounter = 0;
const nextMsgId = () => `msg-${Date.now()}-${++_msgIdCounter}`;

const ChatPanel = ({ projectId, selectedDoc, onDocUpdated, isOpen, onClose }) => {
  const { canEdit, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('ask'); // 'ask' or 'suggest'
  const [pendingSuggestion, setPendingSuggestion] = useState(null);
  const [applyingChanges, setApplyingChanges] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [faqItems, setFaqItems] = useState([]);
  const [faqExpanded, setFaqExpanded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadFaq = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API_URL}/chat/faq?project_id=${projectId}&limit=10`);
      setFaqItems(res.data || []);
    } catch (error) {
      console.error('Failed to load FAQ items:', error);
    }
  }, [projectId]);

  const loadHistory = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API_URL}/chat/history?project_id=${projectId}&limit=15`);
      const history = res.data.reverse().flatMap(h => [
        { id: nextMsgId(), role: 'user', content: h.question, timestamp: h.created_at },
        { id: nextMsgId(), role: 'assistant', content: h.answer, sources: h.sources, timestamp: h.created_at },
      ]);
      setMessages(history);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  }, [projectId]);

  // Load chat history for this project
  useEffect(() => {
    if (isOpen && projectId) {
      loadHistory();
      loadFaq();
    }
  }, [isOpen, projectId, loadHistory, loadFaq]);

  const handleFaqClick = (question) => {
    setInput(question);
    setFaqExpanded(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { id: nextMsgId(), role: 'user', content: userMessage, mode }]);
    setLoading(true);
    setPendingSuggestion(null);

    try {
      if (mode === 'suggest' && selectedDoc) {
        await _handleSuggestMode(userMessage);
        return;
      } else {
        await _handleAskMode(userMessage);
        return;
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to get AI response';
      setMessages(prev => [...prev, {
        id: nextMsgId(),
        role: 'assistant',
        content: `Sorry, I encountered an error: ${errorMsg}`,
        isError: true,
      }]);
    } finally {
      setLoading(false);
    }
  };

  // ---- Suggest-changes mode handler ----
  const _handleSuggestMode = async (userMessage) => {
    const startRes = await axios.post(`${API_URL}/chat/suggest-changes`, {
      doc_id: selectedDoc.id,
      instruction: userMessage,
      include_source_code: true,
    });
    const jobId = startRes.data.job_id;

    setMessages(prev => [...prev, {
      id: nextMsgId(),
      role: 'assistant',
      content: '🔄 Analyzing codebase and generating changes...',
      type: 'processing',
      jobId,
    }]);

    let attempts = 0;
    const maxAttempts = 120;
    const pollMs = 1000;

    const pollJob = async () => {
      attempts++;
      try {
        const statusRes = await axios.get(`${API_URL}/chat/suggest-changes/${jobId}`);
        const job = statusRes.data;

        setMessages(prev => prev.map(msg =>
          msg.jobId === jobId
            ? { ...msg, content: `🔄 ${job.message} (${job.progress}%)` }
            : msg
        ));

        if (job.status === 'completed' && job.result) {
          setMessages(prev => prev.map(msg =>
            msg.jobId === jobId
              ? {
                  ...msg,
                  role: 'assistant',
                  content: job.result.explanation,
                  type: 'suggestion',
                  sourceFilesUsed: job.result.source_files_used,
                }
              : msg
          ));
          setPendingSuggestion({
            content: job.result.suggested_content,
            docId: job.result.doc_id,
            docTitle: job.result.doc_title,
          });
          setLoading(false);
          return;
        } else if (job.status === 'failed') {
          setMessages(prev => prev.map(msg =>
            msg.jobId === jobId
              ? { ...msg, role: 'assistant', content: `❌ ${job.error || job.message}`, type: 'error' }
              : msg
          ));
          setLoading(false);
          toast.error('Failed to generate changes');
          return;
        } else if (attempts < maxAttempts) {
          setTimeout(pollJob, pollMs);
        } else {
          setMessages(prev => prev.map(msg =>
            msg.jobId === jobId
              ? { ...msg, role: 'assistant', content: '⏱️ Request timed out. Please try again.', type: 'error' }
              : msg
          ));
          setLoading(false);
          toast.error('Request timed out');
        }
      } catch (pollError) {
        console.error('Poll error:', pollError);
        if (attempts < maxAttempts) {
          setTimeout(pollJob, pollMs);
        } else {
          setMessages(prev => prev.map(msg =>
            msg.jobId === jobId
              ? { ...msg, role: 'assistant', content: '❌ Failed to check job status', type: 'error' }
              : msg
          ));
          setLoading(false);
        }
      }
    };

    setTimeout(pollJob, pollMs);
  };

  // ---- Ask (Q&A streaming) mode handler ----
  const _handleAskMode = async (userMessage) => {
    setMessages(prev => [...prev, {
      id: nextMsgId(),
      role: 'assistant',
      content: '',
      sources: [],
      isStreaming: true,
    }]);
    setLoading(false);

    try {
      if (!token) throw new Error('Authentication token missing');

      const response = await fetch(`${API_URL}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ question: userMessage, project_id: projectId }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      let sources = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'chunk') {
              fullText += data.content;
              setMessages(prev => {
                const updated = [...prev];
                const lastMsg = updated[updated.length - 1];
                if (lastMsg && lastMsg.role === 'assistant') {
                  updated[updated.length - 1] = { ...lastMsg, content: fullText };
                }
                return updated;
              });
            } else if (data.type === 'done') {
              sources = data.sources || [];
              setMessages(prev => {
                const updated = [...prev];
                const lastMsg = updated[updated.length - 1];
                if (lastMsg && lastMsg.role === 'assistant') {
                  updated[updated.length - 1] = { ...lastMsg, content: fullText, sources, isStreaming: false };
                }
                return updated;
              });
            } else if (data.type === 'error') {
              setMessages(prev => {
                const updated = [...prev];
                const lastMsg = updated[updated.length - 1];
                if (lastMsg && lastMsg.role === 'assistant') {
                  updated[updated.length - 1] = { ...lastMsg, content: `Error: ${data.content}`, isError: true, isStreaming: false };
                }
                return updated;
              });
            }
          } catch (e) {
            console.error('Failed to parse SSE line:', e);
          }
        }
      }
    } catch (streamError) {
      // Fallback to non-streaming
      try {
        const res = await axios.post(`${API_URL}/chat`, {
          question: userMessage,
          project_id: projectId,
        });
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            role: 'assistant',
            content: res.data.answer,
            sources: res.data.sources,
            isStreaming: false,
          };
          return updated;
        });
      } catch (fallbackErr) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            role: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.',
            isError: true,
            isStreaming: false,
          };
          return updated;
        });
      }
    }
  };

  const handleApplyChanges = async () => {
    if (!pendingSuggestion) return;
    setApplyingChanges(true);
    try {
      await axios.patch(`${API_URL}/documentation/${pendingSuggestion.docId}/content`, {
        content: pendingSuggestion.content,
        expected_version: selectedDoc?.version || null,
      });
      toast.success('Changes applied to documentation!');
      setPendingSuggestion(null);
      setMessages(prev => [...prev, {
        id: nextMsgId(),
        role: 'system',
        content: 'Changes applied successfully. The document has been updated.',
      }]);
      if (onDocUpdated) onDocUpdated();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('This document was modified by someone else. Please refresh the page and try again.');
        if (onDocUpdated) onDocUpdated();
      } else {
        toast.error('Failed to apply changes');
      }
    } finally {
      setApplyingChanges(false);
    }
  };

  const handleDismissChanges = () => {
    setPendingSuggestion(null);
    setMessages(prev => [...prev, {
      id: nextMsgId(),
      role: 'system',
      content: 'Changes dismissed.',
    }]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 bottom-0 w-full sm:w-[380px] xl:w-[420px] bg-white border-l border-slate-200 shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
      <ChatHeader selectedDoc={selectedDoc} onClose={onClose} />

      {canEdit && selectedDoc && (
        <ChatModeToggle mode={mode} setMode={setMode} />
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {faqItems.length > 0 && messages.length === 0 && (
            <FaqSection
              faqItems={faqItems}
              faqExpanded={faqExpanded}
              setFaqExpanded={setFaqExpanded}
              onFaqClick={handleFaqClick}
            />
          )}

          {messages.length === 0 && (
            <div className="text-center py-12">
              <Bot className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500 mb-1">
                {mode === 'suggest'
                  ? 'Describe what changes you want to make'
                  : 'Ask me anything about this project'}
              </p>
              <p className="text-xs text-slate-400">
                {mode === 'suggest'
                  ? 'e.g., "Add error handling section to the API docs"'
                  : 'e.g., "What database does this use?"'}
              </p>
            </div>
          )}

          {messages.map((msg) => (
            <MessageBubble key={msg.id} msg={msg} />
          ))}

          {loading && (
            <div className="flex gap-2">
              <div className="w-7 h-7 rounded-full bg-[#2B8FD8] flex items-center justify-center shrink-0">
                <Bot className="h-3.5 w-3.5 text-white" />
              </div>
              <div className="bg-slate-100 rounded-xl px-4 py-3 rounded-bl-sm">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {mode === 'suggest' ? 'Generating suggested changes...' : 'Thinking...'}
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <PendingSuggestionCard
        pendingSuggestion={pendingSuggestion}
        previewExpanded={previewExpanded}
        setPreviewExpanded={setPreviewExpanded}
        applyingChanges={applyingChanges}
        onApply={handleApplyChanges}
        onDismiss={handleDismissChanges}
      />

      <ChatInput
        inputRef={inputRef}
        input={input}
        setInput={setInput}
        onSend={handleSend}
        loading={loading}
        mode={mode}
        selectedDoc={selectedDoc}
      />
    </div>
  );
};

export default ChatPanel;
